import type { FValues, Gradients } from ".";
import { GRAVITY_FT_SEC } from "../../constants";
import type { PipeFrictionContext } from "../d";

export function calculateGradientsLiquid(
  context: PipeFrictionContext,
  fValues: FValues
): Gradients {
  const {
    fluidPropertiesContext: {
      fluidProperties: {
        gasDensityLBMFT3,
        liquidDensityLBMFT3,
        gasLiquidRatioSCFSTB
      },
      fluidFrictionProperties: {
        liquidVelocityNumber,
        gasVelocityNumber,
        liquidInterfacialTension,
        pipeDiameterNumber,
        liquidReynoldsNumber,
        inSituLiquidFlowRateCFS,
        inSituGasFlowRateCFS
      }
    },
    epsilonD
  } = context;
  const { F1, F2, F3tick } = fValues;

  // D172: =D142/(D142+D145)
  const inSituLiquidHoldUp =
    inSituLiquidFlowRateCFS / (inSituLiquidFlowRateCFS + inSituGasFlowRateCFS);

  // D221: =D202+D204*D176+D207*(D174/(1+D176))^2
  const slipCorrelation =
    F1 +
    F2 * liquidVelocityNumber +
    F3tick * (gasVelocityNumber / (1 + liquidVelocityNumber)) ** 2;

  // CHEN FRICTION
  // D223: =(1/(-2*LOG10(D305/3.7065-(5.0452/D303)*LOG10(((D305^1.1098)/2.8257)+(5.8506/(D303^0.8981))))))^2
  const f1 =
    (1 /
      (-2 *
        Math.log10(
          epsilonD / 3.7065 -
            (5.0452 / liquidReynoldsNumber) *
              Math.log10(
                epsilonD ** 1.1098 / 2.8257 +
                  5.8506 / liquidReynoldsNumber ** 0.8981
              )
        ))) **
    2;
  // D225: =D223*D42*D178^(2/3)
  const f2 = f1 * gasLiquidRatioSCFSTB * pipeDiameterNumber ** (2 / 3);

  // D227: =1+D223*SQRT(D42/50)
  const f3 = 1 + f1 * Math.sqrt(gasLiquidRatioSCFSTB / 50);

  // D229: =D223*(D225/D227)
  const fw = f1 * (f2 / f3);

  // D231: =2*D229*(D176*(D176+D174))/D178
  const frictionGradient =
    (2 *
      fw *
      (liquidVelocityNumber * (liquidVelocityNumber + gasVelocityNumber))) /
    pipeDiameterNumber;

  // D233: =D221/(D63/(D166*D67))^0.25
  const slipVelocity =
    slipCorrelation /
    (liquidDensityLBMFT3 / (liquidInterfacialTension * GRAVITY_FT_SEC)) ** 0.25;

  // D235: =D172
  const liquidHoldup = inSituLiquidHoldUp;

  // D237: =(D235*D63+(1-D235)*D62)/(144)
  const staticGradient =
    (liquidHoldup * liquidDensityLBMFT3 +
      (1 - liquidHoldup) * gasDensityLBMFT3) /
    144;

  return {
    slipCorrelation,
    f1,
    f2,
    f3,
    fw,
    frictionGradient,
    slipVelocity,
    liquidHoldup,
    staticGradient
  };
}
